import _ from 'lodash';
import {reportBug} from '@packages/utilities/reporter';
import * as StringUtils from '@packages/utilities/string-utils';
import {hasValue} from '@packages/systems/core/utils/JsUtils';
import * as CssUtils from '@packages/systems/style-legacy/utils';
import {
  removeWfTokens,
  replaceExternalTokens,
} from '@packages/systems/core/utils/EmbedUtils/all';
import SharedConfig from '@packages/systems/core/constants/SharedConfig';
import htmlparser from 'htmlparser2';
import {
  getUrlsFromSrcsetVal,
  s3UrlToCdn,
  s3UrlToSslAcceleratedCdn,
} from '@packages/systems/core/utils/SrcsetUtils';
import {html as beautifyHtml} from 'js-beautify';
import {isImageAsset} from '@packages/domains/assets/utilities';
import {getUploadData} from '@packages/utilities/file-upload';
import {isWebflowImage} from '@packages/systems/core/utils/UrlUtils';
import type {AssetState} from '@packages/systems/designer/assets';
import type {PageRecordType} from '@packages/systems/core/records/PageRecord';
import type {FileRecordType} from '@packages/systems/core/records/FileRecord';

export {hasScript, cleanHref} from '@packages/systems/core/utils/HtmlUtils';

export const WFID_ATTR = SharedConfig.WEBFLOW_NODE_ID_ATTR;
export const WF_INSTANCEID_ATTR = SharedConfig.webflowNodeInstanceIdAttr;
export const VOID_HTML_TAGS = SharedConfig.VOID_HTML_TAGS;
export const WF_DATA_ID_ATTR = SharedConfig.WEBFLOW_NODE_ID_PATH_ATTR;

export type DocNameMapType = {
  [docName: string]: string;
};
export type ImageNameMapType = {
  [imageName: string]: string;
};

/**
 * Beautifies a block of HTML.
 * @param  {String} html The block of HTML.
 * @return {String}      The beautified version of the same HTML.
 */
export function beautify(html: string) {
  try {
    html = beautifyHtml(html, {
      indent_inner_html: false, // Indent <head> and <body> sections
      indent_size: 2,
      indent_char: ' ',
      wrap_line_length: 0, // disabled
      preserve_newlines: true,
      // Inner line breaks are preserved for these elements
      content_unformatted: ['pre', 'script', 'style'],
      // Surrounding line breaks skipped with these (inline) elements
      // copied from here, minus 'address' and 'a' https://github.com/beautify-web/js-beautify/blob/e60611044b74021b2db2ccb8c34f0e63705949f8/js/src/html/options.js#L53
      inline: [
        // https://www.w3.org/TR/html5/dom.html#phrasing-content
        'abbr',
        'area',
        'audio',
        'b',
        'bdi',
        'bdo',
        'br',
        'button',
        'canvas',
        'cite',
        'code',
        'data',
        'datalist',
        'del',
        'dfn',
        'em',
        'embed',
        'i',
        'iframe',
        'img',
        'input',
        'ins',
        'kbd',
        'keygen',
        'label',
        'map',
        'mark',
        'math',
        'meter',
        'noscript',
        'object',
        'output',
        'progress',
        'q',
        'ruby',
        's',
        'samp',
        // 'script',
        'select',
        'small',
        'span',
        'strong',
        'sub',
        'sup',
        'svg',
        'template',
        'textarea',
        'time',
        'u',
        'var',
        'video',
        'wbr',
        'text',
        // prexisting - not sure of full effect of removing, leaving in
        'acronym',
        // Commenting out address because it is in lib's defaults but is not
        // classified as phrasing-content or an inline element. Fixes #7010
        // 'address',
        'big',
        'dt',
        'ins',
        'strike',
        'tt',
      ],
    });
  } catch (err: any) {
    // Please see https://github.com/webflow/webflow/pull/39900
    // eslint-disable-next-line webflow/better-bug-reporting
    reportBug('HtmlBeautifyError', 'Failed during export', {
      error: err.message,
      stack: err.stack,
      html,
    });
    // Return the original HTML
    return html;
  }
  return html;
}

/**
 * Cleans a block of HTML for export.
 * @param  {String}     opts.pageHtml      The page HTML to clean.
 * @param  {AssetState} opts.assetState    The AssetStore state needed to resolve image guids.
 * @param  {String}     opts.pageSlug      The slug of the page from which we need a relative path, e.g. "about/team".
 * @param  {Object[]}   opts.allPages      The list of all pages to account for when relativizing links (optional).
 * @param  {Object}     opts.imageNameMap  A lookup table used to substitute filenames.
 * @param  {Object}     opts.docNameMap    A lookup table used to substitute doc names.
 * @param  {Boolean}    opts.escape        Whether to escape() the HTML at the end. (optional)
 * @return {String}                        The cleaned HTML.
 */
export function cleanForExport({
  pageHtml: html,
  assetState,
  pageSlug,
  allPages,
  imageNameMap,
  docNameMap,
  escape: _escape,
  videoFiles = [],
}: {
  allPages?: Array<PageRecordType>;
  assetState: AssetState;
  docNameMap: DocNameMapType;
  escape?: boolean;
  imageNameMap: ImageNameMapType;
  pageHtml: string;
  pageSlug: string;
  videoFiles: Array<FileRecordType>;
}) {
  if (typeof html !== 'string' || !assetState) {
    throw new Error('cleanForExport missing required arguments!');
  }

  if (allPages) {
    html = relativizePageLinks(html, pageSlug, allPages);
  }

  const relativeRootPath = getRelativeRootPath(pageSlug);
  html = removeDynAttributes(html);
  html = removeCustomCodeWrappers(html);
  html = removeWfTokens(html);
  html = replaceExternalTokens(html);
  html = relativizeAssets(html, relativeRootPath);
  html = shortenImageLinks(
    html,
    relativeRootPath,
    imageNameMap,
    docNameMap,
    assetState
  );
  html = shortenVideoLinks(html, relativeRootPath + 'videos/', videoFiles);
  html = absolutizeProtocols(html);
  html = enablePHP(html);
  html = StringUtils.collapseNewLines(html);

  if (_escape) {
    html = escape(html);
  }

  return html;
}

/**
 * Removes width and height attributes from iframe HTML.
 * @param  {String} html  A string of HTML.
 * @return {String}       The same string, with width="X", height="Y" stripped out.
 */
export function stripWidthHeight(html: string) {
  const cleaned = html.replace(/[ ]?\b(width|height)="\d+"/g, '');
  return cleaned;
}

/**
 * Removes Webflow GUID attributes from the HTML. Mostly useful for cleaning up markup for comparison in tests.
 * @param  {String} html A string of HTML.
 * @return {String}      The same string, with all data-w-id="*" attributes removed.
 */
export function stripWebflowIds(html: string) {
  return html.replace(
    new RegExp(' (' + WFID_ATTR + '|' + WF_INSTANCEID_ATTR + ')="[^"]*"', 'g'),
    ''
  );
}

/**
 * Converts an full dimension to one that is appropriate for inline attributes for images (e.g. "927px" -> "927", "100%" -> "100%", "auto" -> null)
 * @param  {String} str The input string.
 * @return {String}     The cleaned string.
 */
export function cleanImageDim(str: string) {
  str = _.trim(str);

  if (!str || str === 'auto' || str === 'autopx' || str === 'auto%') {
    return null;
  }

  const parsed = CssUtils.parseIfUnit(str);
  if (
    parsed &&
    typeof parsed === 'object' &&
    hasValue(parsed.value) &&
    hasValue(parsed.unit)
  ) {
    // Preserve percentage
    if (parsed.unit === '%') {
      return str;
    }

    // Otherwise only pass the value
    return parsed.value;
  }

  return str;
}

/**
 * Gets the relative path to the root of the site from a given page's slug.
 * @param  {String} pageSlug The slug of the page from which we need a relative path, e.g. "about/team"
 * @return {String}          The path to the root of the site, e.g. "../"
 */
export function getRelativeRootPath(pageSlug: string) {
  if (!pageSlug) {
    return '';
  }

  const pageDepth = (_.trim(pageSlug || '', '/').match(/\//g) || []).length;
  return _.repeat('../', pageDepth);
}

/**
 * Converts clean absolute URLs (e.g. '/about') to relative export-friendly ones (e.g. 'about.html')
 * @param  {String}   pageHtml    The full block of HTML for this page.
 * @param  {String}   pageSlug    The slug of the current page that we are processing.
 * @param  {Object[]} allPages    The list of all pages to account for.
 * @return {String}               The same HTML with fixed link href values.
 */
export function relativizePageLinks(
  pageHtml: string,
  pageSlug: string,
  allPages: Array<Record<any, any>>
) {
  const rootPath = getRelativeRootPath(pageSlug);

  // Find all HREF="..." in the HTML block
  const cleaned = pageHtml.replace(
    /<a ([^>]*)href="([^"]+)"([^>]*)>/g,
    function (linkTag, beforeHref, oldUrl, afterHref) {
      // Walk through each page and see if the HREF matches any of the page slugs
      for (let i = 0, j = allPages.length; i < j; i++) {
        const otherPage = allPages[i];
        // @ts-expect-error - TS18048 - 'otherPage' is possibly 'undefined'.
        const otherSlug = '/' + (otherPage.slug || '');

        // If the old URL matches this page's slug, replace with the file version
        if (StringUtils.weakEqual(otherSlug, oldUrl)) {
          const slug = oldUrl === '/' ? 'index' : _.trimStart(oldUrl, '/');
          const newUrl = rootPath + slug + '.html';

          return '<a ' + beforeHref + 'href="' + newUrl + '"' + afterHref + '>';
        }
      }

      // Otherwise if the HREF doesn't link to any existing page slugs, leave it alone
      return linkTag;
    }
  );

  return cleaned;
}

/**
 * Converts links to CSS and JS files to relative ones.
 * @param  {String} pageHtml The full block of HTML for a page.
 * @param  {String} rootPath The relative path to the root of the site, e.g. '../../'
 * @return {String}          The same HTML with relativized links to CSS, JS and favicons files.
 */
export function relativizeAssets(pageHtml: string, rootPath: string) {
  rootPath = rootPath || '';

  return pageHtml
    .replace(/src="js\//g, `src="${rootPath}js/`)
    .replace(/href="css\//g, `href="${rootPath}css/`)
    .replace(/href="images\//g, `href="${rootPath}images/`);
}

/**
 * Converts long CDN links to relative links. Used for export.
 * @param  {String}     fullHtml      The block of HTML that can contain inline image blocks.
 * @param  {String}     urlPrefix     The string to prefix to the relative URL, e.g. 'images/'.
 * @param  {Object}     imageNameMap  A lookup table used to substitute filenames.
 * @param  {Object}     docNameMap    A lookup table used to substitute doc names.
 * @param  {AssetState} assetState    The Flux AssetState to use to look up images.
 * @return {String}                   The same HTML with absolute CDN links converted to localized ones.
 */
function shortenImageLinks(
  fullHtml: string,
  urlPrefix: string,
  imageNameMap: ImageNameMapType,
  docNameMap: DocNameMapType,
  assetState: AssetState
) {
  imageNameMap = imageNameMap || {};
  docNameMap = docNameMap || {};

  const shortenUrl = function (fullUrl: string) {
    const fileData = getUploadData(fullUrl);
    const asset =
      assetState.getByGuid(fileData.guid) ||
      assetState.getById(fileData.guid) ||
      null;
    if (!asset) {
      console.error('Missing file model: %o', fileData);
      return fullUrl;
    }
    const isImageFile = isImageAsset(asset);
    const folder = isImageFile ? 'images/' : 'documents/';

    // Note:  FileUploadUtils().origFileName is the old/original filename parsed from the full url(which currently doesn't change)
    const parsedOrigFileName = fileData.origFileName;
    const isVariant = /-p-/.test(parsedOrigFileName);
    const cleanedParsedOrigFileName =
      StringUtils.cleanFileNameForExport(parsedOrigFileName);
    const cleanedParsedFileName = StringUtils.cleanFileNameForExport(
      fileData.fileName
    );
    const variants = asset.variants;

    const variantModel =
      isVariant &&
      variants &&
      variants.find((v) => {
        return (
          StringUtils.cleanFileNameForExport(v.fileName) ===
          cleanedParsedFileName
        );
      });

    const origFileName = (() => {
      const collisionProofName = isImageFile
        ? imageNameMap[cleanedParsedFileName]
        : docNameMap[cleanedParsedFileName];

      if (collisionProofName) {
        return collisionProofName;
      } else if (variantModel) {
        return variantModel.origFileName;
      } else if (asset) {
        return asset.origFileName;
      } else {
        return cleanedParsedOrigFileName;
      }
    })();

    // Note: origFileName from the AssetStore is actually the newest filename
    const cleanedOrigFileName =
      StringUtils.cleanFileNameForExport(origFileName);

    // Change to a relative path
    const shortUrl = urlPrefix + folder + cleanedOrigFileName;
    return shortUrl;
  };

  const replaceWebflowUrl = (str: any, fullUrl: string) => {
    if (isWebflowImage(fullUrl)) {
      const fullUrlRegExp = new RegExp(_.escapeRegExp(fullUrl), 'g');
      const shortUrl = shortenUrl(fullUrl);
      return str.replace(fullUrlRegExp, shortUrl);
    } else {
      return fullUrl;
    }
  };

  const cleanedAlt: Array<any | string> = [];
  const dataSrc = 'data-src';
  const dataAnimType = 'data-animation-type';
  let currentTag = '';
  // NOTE: callbacks are called in the following order: onopentagname -> onattribute (n times) -> onopentag;
  const parserAlt = new htmlparser.Parser({
    onopentag: (tag, attr) => {
      currentTag = tag;

      if (tag === 'img') {
        const src = attr.src;
        const srcset = attr.srcset;

        if (src) {
          attr.src = replaceWebflowUrl(src, src);
        }

        if (srcset) {
          getUrlsFromSrcsetVal(srcset).forEach((fullUrl) => {
            attr.srcset = replaceWebflowUrl(attr.srcset, fullUrl);
          });
        }
      } else if (tag === 'link' || tag === 'a') {
        const href = attr.href;

        if (href) {
          attr.href = replaceWebflowUrl(href, href);
        }
      } else if (attr[dataAnimType] === 'lottie' && attr[dataSrc]) {
        const src = attr[dataSrc];
        attr[dataSrc] = replaceWebflowUrl(src, src);
      }

      const attrString = attrToString(attr);
      cleanedAlt.push('<' + tag + (attrString ? ' ' + attrString : '') + '>');
    },
    ontext: (text) => {
      if (currentTag === 'script') {
        // Replace static lightbox images to point to relative image instead of cloudfront
        // url is the old source of truth; cdnUrl is the new one
        text = text.replace(
          /"(?:url|cdnUrl)": "(https:\/\/[a-z0-9-]+\.(?:webflow\.com|cloudfront\.net)\/[^"]+)"/g,
          function (tag, fullUrl) {
            if (isWebflowImage(fullUrl)) {
              const shortUrl = shortenUrl(fullUrl);
              return tag.replace(fullUrl, shortUrl);
            } else {
              // Leave other external images alone
              return tag;
            }
          }
        );
      }
      cleanedAlt.push(text);
    },
    // @ts-expect-error - TS2322 - Type '(tag: any) => void' is not assignable to type '() => void'. | TS7006 - Parameter 'tag' implicitly has an 'any' type.
    onclosetag: (tag) => {
      if (!VOID_HTML_TAGS.has(tag)) {
        cleanedAlt.push('</' + tag + '>');
      }
    },
    oncomment: (comment) => {
      cleanedAlt.push('<!-- ' + comment + ' -->');
    },
    onprocessinginstruction: (name, data) => {
      cleanedAlt.push('<' + data + '>');
    },
    onerror: (error) => {
      // Please see https://github.com/webflow/webflow/pull/39900
      // eslint-disable-next-line webflow/better-bug-reporting
      reportBug('shortenImageLinksError', 'Failed during export', {
        error: error.message,
        stack: error.stack,
      });
    },
  });

  parserAlt.parseChunk(fullHtml);
  parserAlt.done();

  return cleanedAlt.join('');
}

/**
 * Converts an attribute object to an name="value" string
 * @param  {Object} attr an object of html attributes
 * @return {String}      a string representation of html attributes
 */
const attrToString = (attr: any) => {
  const attrStringParts: Array<any | string> = [];
  for (const name in attr) {
    attrStringParts.push(`${name}="${attr[name]}"`);
  }

  return attrStringParts.join(' ');
};

/**
 * Replaces CDN links with relative links throughout the provided HTML string.
 * @param  {String}     fullHtml      The block of HTML that can contain inline image blocks.
 * @param  {String}     urlPrefix     The string to prefix to the relative URL, e.g. 'images/'.
 * @param  {List}       videoFiles    Files to use for replacement.
 * @return {String}
 */
const shortenVideoLinks = (
  fullHtml: string,
  urlPrefix: string,
  videoFiles: Array<FileRecordType>
): string => {
  function shortenLinkInHtml(
    html: string,
    videoFileOrVariant: {origFileName: string; s3Url: string}
  ) {
    // we should replace with the variant's original file name - this is what
    // will be used as the file name in the zip export file
    const relativeFileLink = `${urlPrefix}${StringUtils.cleanFileNameForExport(
      videoFileOrVariant.origFileName
    )}`;

    // try to shorten using both the cdn and cdn ssl accelerated url since we're not sure which one will be
    // available in the html
    const cdnUrl = s3UrlToCdn(videoFileOrVariant.s3Url);
    const cdnSslAcceleratedUrl = s3UrlToSslAcceleratedCdn(
      videoFileOrVariant.s3Url
    );

    return html
      .split(cdnUrl)
      .join(relativeFileLink)
      .split(cdnSslAcceleratedUrl)
      .join(relativeFileLink);
  }

  // extract the cdn url and original file name for all files associated with this video. This will include the original
  // file as well as it's encoded variants (including the poster image)
  const videoFilesWithVariants = videoFiles.flatMap((videoFile) => {
    const currentVideoFileWithVariants = [
      {
        s3Url: videoFile.s3Url,
        origFileName: videoFile.origFileName,
      },
    ];

    for (const variant of videoFile.variants) {
      currentVideoFileWithVariants.push({
        // video variants are created with an ssl accelerated CDN url during the
        // encoding process so we need to make sure we're matching that same url here
        s3Url: variant.s3Url,
        origFileName: variant.origFileName,
      });
    }

    return currentVideoFileWithVariants;
  });

  return videoFilesWithVariants.reduce(shortenLinkInHtml, fullHtml);
};

/**
 * Converts protocol-relative SRC urls to HTTPS.
 * @param  {String} fullHtml The raw HTML.
 * @return {String}          The HTML with all "//some.domain.com" replaced with "https://some.domain.com"
 */
export function absolutizeProtocols(fullHtml: string) {
  return fullHtml.replace(/ src="\/\//g, ' src="https://');
}

/**
 * Uncomments PHP blocks (should only be used for export). Need to do this because the server XML builder comments PHP blocks out.
 * @param  {String} fullHtml The HTML, possibly with PHP blocks commented out.
 * @return {String}          The cleaned up HTML.
 */
export function enablePHP(fullHtml: string) {
  return fullHtml.replace(
    /<!--\?php ([^\?]*) \?-->/g,
    function (phpBlock, innerContents) {
      return '<?php ' + innerContents + ' ?>';
    }
  );
}

/**
 * Remove any attributes within the HTML that start with dyn-*
 * @param  {String} fullHtml HTML potentially with Dynamic Attributes
 * @return {String}          Cleaned HTML
 */
function removeDynAttributes(fullHtml: string) {
  return fullHtml.replace(/[ ]dyn-[a-zA-Z0-9\.\-_]*(="[^"]*")?/g, '');
}

/**
 * Remove any custom code wrappers within the HTML
 * @param  {String} fullHtml HTML potentially with custom code wrapper
 * @return {String}          Cleaned HTML
 */
function removeCustomCodeWrappers(fullHtml: string) {
  return fullHtml.replace(/<(\/)?custom-code-wrapper>/g, '');
}

/**
 * Basic check to see if a block of HTML has an iframe tag.
 * @param  {String}  html The block of HTML.
 * @return {Boolean}      True if it looks like there is an iframe in the HTML.
 */
export function hasIframe(html: string) {
  if (!html) {
    return false;
  }

  return /<[^>]*iframe/.test(html.toLowerCase());
}

/**
 * Test if URL is relative or full with domain.
 * @param  {String}  url Input URL string
 * @return {Boolean}
 */
export function isExternalUrl(url: string) {
  return /^(https|http):\/\//i.test(url);
}
